"use client";
import { config } from "@repo/config";
import { getAwsCredentialsAction } from "@saas/admin/lib/aws/actions";
import type { AwsCredentials } from "@saas/shared/types/viewing";
import { useCallback, useEffect, useState } from "react";
export interface AwsCredentialsState {
  credentials: AwsCredentials | null;
  identityId: string | null;
  developerUserIdentifier: string | null;
  loading: boolean;
  error: string | null;
  lastExchanged: Date | null;
}
export interface UseAwsCredentialsReturn extends AwsCredentialsState {
  exchangeCredentials: () => Promise<AwsCredentialsState | null>;
  clearCredentials: () => void;
  isExpired: () => boolean;
}
interface CredentialStore {
  credentials: AwsCredentials | null;
  identityId: string | null;
  developerUserIdentifier: string | null;
  lastExchanged: Date | null;
  userId: string | null;
  organizationId: string | null;
}
const empty = (): CredentialStore => ({
  credentials: null,
  identityId: null,
  developerUserIdentifier: null,
  lastExchanged: null,
  userId: null,
  organizationId: null,
});
let store = empty();
let generation = 0;
let inflight: { key: string; promise: Promise<CredentialStore | null> } | null = null;
const subscribers = new Set<() => void>();
const notify = () => {
  subscribers.forEach((callback) => callback());
};
function removeLegacyCache() {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem("aws_credentials_cache");
      localStorage.removeItem("aws_credentials_org");
    } catch {}
  }
}
// Credentials are memory-only. Persisting them across login sessions loses the authority boundary.
removeLegacyCache();
export function clearAwsCredentials() {
  generation++;
  inflight = null;
  store = empty();
  removeLegacyCache();
  notify();
}
export function broadcastAuthContextChange() {
  clearAwsCredentials();
  try {
    localStorage.setItem("organization-switch", crypto.randomUUID());
  } catch {}
}
export function setAwsCredentialContext(userId: string | null, organizationId: string | null) {
  if (store.userId === userId && store.organizationId === organizationId) return;
  clearAwsCredentials();
  store = { ...empty(), userId, organizationId };
}
function expired() {
  return (
    !store.credentials?.expiration ||
    new Date(store.credentials.expiration).getTime() - Date.now() <= 300000
  );
}
export async function exchangeCredentialsForContext(
  userId: string | null,
  organizationId: string | null
): Promise<CredentialStore | null> {
  setAwsCredentialContext(userId, organizationId);
  if (!userId || !organizationId) return null;
  if (store.credentials && !expired()) return store;
  const key = JSON.stringify([userId, organizationId, generation]);
  if (inflight?.key === key) return inflight.promise;
  const started = generation;
  const promise = (async () => {
    const result = await getAwsCredentialsAction({
      identityPoolId: config.aws.identityPoolId,
      region: config.aws.region,
      developerProviderName: config.aws.developerProviderName,
      organizationId,
    });
    if (
      started !== generation ||
      store.userId !== userId ||
      store.organizationId !== organizationId
    )
      return null;
    store = {
      userId,
      organizationId,
      identityId: result.identityId,
      developerUserIdentifier: result.developerUserIdentifier,
      lastExchanged: new Date(),
      credentials: result.credentials
        ? ({
            ...result.credentials,
            expiration: result.credentials.expiration
              ? new Date(result.credentials.expiration)
              : undefined,
          } as AwsCredentials)
        : null,
    };
    notify();
    return store;
  })();
  inflight = { key, promise };
  try {
    return await promise;
  } finally {
    if (inflight?.key === key) inflight = null;
  }
}
export function useAwsCredentials(
  userId?: string | null,
  organizationId?: string | null,
  autoExchange = config.aws.autoExchangeCredentialsOnLogin
): UseAwsCredentialsReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, refresh] = useState(0);
  useEffect(() => {
    const listener = () => refresh((n) => n + 1);
    subscribers.add(listener);
    return () => {
      subscribers.delete(listener);
    };
  }, []);
  const exchangeCredentials = useCallback(async (): Promise<AwsCredentialsState | null> => {
    setLoading(true);
    setError(null);
    try {
      const next = await exchangeCredentialsForContext(userId ?? null, organizationId ?? null);
      return next ? { ...next, loading: false, error: null } : null;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to get credentials");
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, organizationId]);
  useEffect(() => {
    setAwsCredentialContext(userId ?? null, organizationId ?? null);
    setError(null);
    if (autoExchange && userId && organizationId) void exchangeCredentials();
  }, [userId, organizationId, autoExchange, exchangeCredentials]);
  useEffect(() => {
    if (!autoExchange || !userId || !organizationId) return;
    const timer = setInterval(
      () => {
        void exchangeCredentials();
      },
      5 * 60 * 1000
    );
    return () => clearInterval(timer);
  }, [autoExchange, userId, organizationId, exchangeCredentials]);
  const matches =
    store.userId === (userId ?? null) && store.organizationId === (organizationId ?? null);
  const visible = matches ? store : empty();
  return {
    ...visible,
    loading,
    error,
    exchangeCredentials,
    clearCredentials: clearAwsCredentials,
    isExpired: expired,
  };
}
export function seedAwsCredentials(data: {
  credentials: AwsCredentials;
  identityId: string;
  developerUserIdentifier: string;
  userId: string;
  organizationId: string;
}) {
  setAwsCredentialContext(data.userId, data.organizationId);
  generation++;
  inflight = null;
  store = {
    ...data,
    lastExchanged: new Date(),
    credentials: {
      ...data.credentials,
      expiration: data.credentials.expiration ? new Date(data.credentials.expiration) : undefined,
    } as AwsCredentials,
  };
  notify();
}
export function getCurrentAwsCredentials(): AwsCredentials | null {
  return expired() ? null : store.credentials;
}
export function getCurrentIdentityId(): string | null {
  return expired() ? null : store.identityId;
}
